import { supabase } from '@/lib/supabase';

type RpcError = { code?: string; message?: string };
type RpcResult<T> = { data: T | null; error: RpcError | null };
type RpcClient = {
  rpc: (name: string, params?: Record<string, unknown>) => Promise<RpcResult<unknown>>;
};

const rpcClient = supabase as unknown as RpcClient;

export type SearchSectionKey = 'recent' | 'trending' | 'recommend' | 'promoted';

export interface SearchSectionSetting {
  enabled: boolean;
  sortOrder: number;
}

export interface SearchDropdownSettings {
  dropdownEnabled: boolean;
  configVersion: number;
  sections: Record<SearchSectionKey, SearchSectionSetting>;
}

const DEFAULT_SECTIONS: Record<SearchSectionKey, SearchSectionSetting> = {
  recent: { enabled: true, sortOrder: 0 },
  trending: { enabled: true, sortOrder: 1 },
  recommend: { enabled: true, sortOrder: 2 },
  promoted: { enabled: true, sortOrder: 3 },
};

function createSearchAdminError(error: unknown): Error {
  const errorLike = error as RpcError | null;
  const message = String(errorLike?.message || '');
  const code = String(errorLike?.code || '');
  const lower = message.toLowerCase();

  if (code === '42501' || lower.includes('access denied')) {
    return new Error('This account is not authorized for search curation. Ask the owner to add this Supabase user to analytics_admin_users.');
  }
  return new Error(message || 'Search curation request failed');
}

async function callRpc<T>(name: string, params?: Record<string, unknown>): Promise<T> {
  const { data, error } = await rpcClient.rpc(name, params);
  if (error) throw createSearchAdminError(error);
  return data as T;
}

export interface SearchSynonym {
  alias: string;
  expansion: string;
  enabled: boolean;
  updatedAt: string | null;
}

export interface SearchSuggestion {
  id: string;
  section: 'recommend' | 'promoted';
  label: string;
  query: string | null;
  productId: string | null;
  sortOrder: number;
  enabled: boolean;
  updatedAt: string | null;
}

type SynonymRpcRow = {
  alias: string;
  expansion: string;
  enabled: boolean;
  updated_at: string | null;
};

type SuggestionRpcRow = {
  id: string;
  section: string;
  label: string;
  query: string | null;
  product_id: string | null;
  sort_order: number;
  enabled: boolean;
  updated_at: string | null;
};

function mapSynonym(row: SynonymRpcRow): SearchSynonym {
  return {
    alias: row.alias,
    expansion: row.expansion,
    enabled: Boolean(row.enabled),
    updatedAt: row.updated_at,
  };
}

function mapSuggestion(row: SuggestionRpcRow): SearchSuggestion {
  return {
    id: row.id,
    section: row.section === 'promoted' ? 'promoted' : 'recommend',
    label: row.label,
    query: row.query,
    productId: row.product_id,
    sortOrder: Number(row.sort_order) || 0,
    enabled: Boolean(row.enabled),
    updatedAt: row.updated_at,
  };
}

function mapSettings(raw: any): SearchDropdownSettings {
  const sectionsIn = (raw && raw.sections) || {};
  const sections = { ...DEFAULT_SECTIONS };
  (Object.keys(DEFAULT_SECTIONS) as SearchSectionKey[]).forEach((key) => {
    const row = sectionsIn[key] || {};
    sections[key] = {
      enabled: row.enabled !== false,
      sortOrder: Number.isFinite(Number(row.sort_order))
        ? Number(row.sort_order)
        : DEFAULT_SECTIONS[key].sortOrder,
    };
  });
  return {
    dropdownEnabled: raw?.dropdown_enabled !== false,
    configVersion: Number(raw?.config_version) || 0,
    sections,
  };
}

export async function getSearchDropdownSettings(): Promise<SearchDropdownSettings> {
  const raw = await callRpc<any>('get_search_dropdown_settings');
  return mapSettings(raw);
}

export async function updateSearchDropdownSettings(input: {
  dropdownEnabled?: boolean;
  sections?: Record<SearchSectionKey, SearchSectionSetting>;
}): Promise<SearchDropdownSettings> {
  const sectionsPayload = input.sections
    ? Object.fromEntries(
        (Object.keys(input.sections) as SearchSectionKey[]).map((key) => [
          key,
          {
            enabled: input.sections![key].enabled !== false,
            sort_order: input.sections![key].sortOrder,
          },
        ]),
      )
    : null;

  const raw = await callRpc<any>('update_search_dropdown_settings', {
    p_dropdown_enabled: input.dropdownEnabled ?? null,
    p_sections: sectionsPayload,
  });
  return mapSettings(raw);
}

export async function listSearchSynonyms(): Promise<SearchSynonym[]> {
  const rows = await callRpc<SynonymRpcRow[]>('list_search_synonyms');
  return (rows || []).map(mapSynonym);
}

export async function upsertSearchSynonym(input: {
  alias: string;
  expansion: string;
  enabled?: boolean;
}): Promise<SearchSynonym> {
  const row = await callRpc<SynonymRpcRow>('upsert_search_synonym', {
    p_alias: input.alias,
    p_expansion: input.expansion,
    p_enabled: input.enabled ?? true,
  });
  return mapSynonym(row as unknown as SynonymRpcRow);
}

export async function deleteSearchSynonym(alias: string): Promise<void> {
  await callRpc('delete_search_synonym', { p_alias: alias });
}

export async function listSearchSuggestions(): Promise<SearchSuggestion[]> {
  const rows = await callRpc<SuggestionRpcRow[]>('list_search_suggestions');
  return (rows || []).map(mapSuggestion);
}

export async function upsertSearchSuggestion(input: {
  id?: string | null;
  section: 'recommend' | 'promoted';
  label: string;
  query?: string | null;
  productId?: string | null;
  sortOrder?: number;
  enabled?: boolean;
}): Promise<SearchSuggestion> {
  const row = await callRpc<SuggestionRpcRow>('upsert_search_suggestion', {
    p_id: input.id || null,
    p_section: input.section,
    p_label: input.label,
    p_query: input.query ?? null,
    p_product_id: input.productId ?? null,
    p_sort_order: input.sortOrder ?? 0,
    p_enabled: input.enabled ?? true,
  });
  return mapSuggestion(row as unknown as SuggestionRpcRow);
}

export async function deleteSearchSuggestion(id: string): Promise<void> {
  await callRpc('delete_search_suggestion', { p_id: id });
}

export interface TrendingPreviewRow {
  query: string;
  eventCount: number;
  sessionCount: number;
  isBlacklisted: boolean;
}

export interface TrendingBlacklistRow {
  query: string;
  createdAt: string | null;
}

type TrendingPreviewRpcRow = {
  query: string;
  event_count: number;
  session_count: number;
  is_blacklisted: boolean;
};

type TrendingBlacklistRpcRow = {
  query: string;
  created_at: string | null;
};

export async function listTrendingPreview(): Promise<TrendingPreviewRow[]> {
  const rows = await callRpc<TrendingPreviewRpcRow[]>('list_search_trending_preview', {
    p_days: 7,
    p_min_count: 3,
    p_min_sessions: 2,
    p_limit: 20,
  });
  return (rows || []).map((row) => ({
    query: row.query,
    eventCount: Number(row.event_count) || 0,
    sessionCount: Number(row.session_count) || 0,
    isBlacklisted: Boolean(row.is_blacklisted),
  }));
}

export async function listTrendingBlacklist(): Promise<TrendingBlacklistRow[]> {
  const rows = await callRpc<TrendingBlacklistRpcRow[]>('list_search_trending_blacklist');
  return (rows || []).map((row) => ({
    query: row.query,
    createdAt: row.created_at,
  }));
}

export async function hideTrendingTerm(query: string): Promise<void> {
  await callRpc('add_search_trending_blacklist', { p_query: query });
}

export async function unhideTrendingTerm(query: string): Promise<void> {
  await callRpc('remove_search_trending_blacklist', { p_query: query });
}

export async function resetTrendingBlacklist(): Promise<void> {
  await callRpc('reset_search_trending_blacklist');
}
